import { A11yModule } from '@angular/cdk/a11y';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { ButtonDirective } from './button.directive';

/**
 * Confirmation body rendered inside `lib-alert-dialog` — a compact card with a variant-tinted
 * icon, a title + description, and Cancel / Confirm actions. `variant="destructive"` swaps the
 * icon + confirm button to the danger colours. Dark-mode aware.
 *
 * Texts left unset by the caller fall back to the transloco keys `shared.alertDialog.*` (root
 * dictionaries of each app). A value passed explicitly always wins.
 */
@Component({
  selector: 'lib-alert',
  standalone: true,
  imports: [ButtonDirective, A11yModule, TranslocoPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="w-[26rem] max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-white p-5 shadow-xl dark:border-slate-700 dark:bg-slate-800">
      <div class="flex items-start gap-3.5">
        <span class="flex size-10 shrink-0 items-center justify-center rounded-full" [class]="iconWrapClass()">
          @if (variant() === 'destructive') {
            <svg class="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          } @else {
            <svg class="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
            </svg>
          }
        </span>
        <div class="min-w-0 flex-1 pt-0.5">
          <h3 class="text-base font-semibold text-gray-900 dark:text-slate-100">
            {{ title() ?? ('shared.alertDialog.title' | transloco) }}
          </h3>
          <p class="mt-1 text-sm leading-relaxed text-gray-500 dark:text-slate-400">
            {{ description() ?? ('shared.alertDialog.description' | transloco) }}
          </p>
        </div>
      </div>

      <div class="mt-5 flex justify-end gap-2">
        <button libBtn variant="outline" (click)="onClose()" (keydown.enter)="onClose()">
          {{ cancelLabel() ?? ('shared.alertDialog.cancel' | transloco) }}
        </button>
        <button
          libBtn
          [variant]="variant() === 'destructive' ? 'destructive' : 'default'"
          (click)="onConfirm()"
          (keydown.enter)="onConfirm()"
          cdkFocusInitial>
          {{ confirmLabel() ?? ('shared.alertDialog.confirm' | transloco) }}
        </button>
      </div>
    </div>
  `,
  host: {
    class: 'block',
  },
})
export class AlertComponent {
  /** Unset (`undefined`) → `shared.alertDialog.*` translation. */
  title = input<string | undefined>(undefined);
  description = input<string | undefined>(undefined);
  confirmLabel = input<string | undefined>(undefined);
  cancelLabel = input<string | undefined>(undefined);
  variant = input<'default' | 'destructive'>('default');
  confirm = output<void>();
  closed = output<void>();

  protected readonly iconWrapClass = computed(() =>
    this.variant() === 'destructive'
      ? 'bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-400'
      : 'bg-primary-50 text-primary-600 dark:bg-primary-500/15 dark:text-primary-400'
  );

  onConfirm() {
    this.confirm.emit();
  }

  onClose() {
    this.closed.emit();
  }
}
